import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateServiceCategoryInput,
  PaginatedResult,
  ServiceCategory,
  SortDirection,
  UpdateServiceCategoryInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface ServiceCategoryListParams {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const serviceCategoryKeys = {
  all: ['service-categories'] as const,
  list: (params: ServiceCategoryListParams) => ['service-categories', 'list', params] as const,
  detail: (id: string) => ['service-categories', 'detail', id] as const,
};

export function useServiceCategories(
  params: ServiceCategoryListParams,
): UseQueryResult<PaginatedResult<ServiceCategory>> {
  return useQuery({
    queryKey: serviceCategoryKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<ServiceCategory>>(
        `/service-categories${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          isActive: params.isActive,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

/** Convenience hook: returns the active service categories ordered by name (for selects/tables). */
export function useServiceCategoryOptions(): UseQueryResult<ServiceCategory[]> {
  return useQuery({
    queryKey: ['service-categories', 'options'],
    queryFn: () => api.get<ServiceCategory[]>('/service-categories/options'),
  });
}

export function useCreateServiceCategory(): UseMutationResult<
  ServiceCategory,
  unknown,
  CreateServiceCategoryInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateServiceCategoryInput) =>
      api.post<ServiceCategory>('/service-categories', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: serviceCategoryKeys.all });
    },
  });
}

export interface UpdateServiceCategoryArgs {
  id: string;
  input: UpdateServiceCategoryInput;
}

export function useUpdateServiceCategory(): UseMutationResult<
  ServiceCategory,
  unknown,
  UpdateServiceCategoryArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateServiceCategoryArgs) =>
      api.patch<ServiceCategory>(`/service-categories/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: serviceCategoryKeys.all });
    },
  });
}

export function useDeleteServiceCategory(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/service-categories/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: serviceCategoryKeys.all });
    },
  });
}
